import type { ReactNode } from "react";

import { Button } from "./Button";
import { Dialog } from "./Dialog";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  /** One sentence that says what will happen. */
  message?: ReactNode;
  /** More detail under the sentence (a list of what changes, for example). */
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** The step is being sent: both buttons wait. */
  busy?: boolean;
  /** A line in plain words when the step was refused. */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Asks before a step that cannot be taken back. Focus starts on the cancel button, so a stray
 * Enter never goes ahead.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  children,
  confirmLabel = "Yes, go ahead",
  cancelLabel = "No, go back",
  busy = false,
  error,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title={title}
      size="small"
      footer={
        <>
          <Button data-autofocus onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant="primary" onClick={onConfirm} busy={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && <p>{message}</p>}
      {children}
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
    </Dialog>
  );
}
